import * as React from 'react';
import {
    ReleaseType,
    ReleaseStatus,
    ReleaseTimelinePhase,
    AppStoreAvailableBuild,
    CompactFeaturesStepData,
    DestinationPhasedRelease,
    Statistic,
    ReleaseStepType,
    StatisticType,
    Integration,
} from '../types';
import { StepRunStatusData } from './stepRunStatus';
import { Destination } from './destinations';
import { ColumnType } from 'antd/lib/table';

export enum PageViewType {
    Default = 'default',
    Tabular = 'tabular',
}

export enum TabularLayoutType {
    Standard = 'standard',
    Compact = 'compact',
}

export enum PageViewContext {
    AppHome = 'appHome',
    OrgOverview = 'orgOverview',
}

export const PAGE_VIEW_LABELS: Record<PageViewContext, { default: string; tabular: string }> = {
    [PageViewContext.AppHome]: { default: 'Summary', tabular: 'Tabular' },
    [PageViewContext.OrgOverview]: { default: 'Stats', tabular: 'Releases' },
};

export type CombinedLayoutValue = 'summary' | 'standard' | 'compact';

export const ARCHETYPE_PREFIX = 'Archetype';
export const STATISTIC_PREFIX = 'Statistic';

export const ArchetypeSummaryViewProperty = `${ARCHETYPE_PREFIX}:${ReleaseStepType}`;
export const StatisticSummaryViewProperty = `${STATISTIC_PREFIX}:${StatisticType}`;

export enum SummaryViewPropertyBase {
    WorkflowRunVersion = 'WorkflowRunVersion',
    Status = 'Status',
    ReleasePilot = 'ReleasePilot',
    FeatureCompletePercent = 'FeatureCompletePercent',
    TargetKickoffDates = 'TargetKickoffDates',
    TargetSubmitDates = 'TargetSubmitDates',
    TargetReleaseDates = 'TargetReleaseDates',
    KickedOffDate = 'KickedOffDate',
    SubmittedDate = 'SubmittedDate',
    ReleasedAtDate = 'ReleasedAtDate',
    Builds = 'Builds',
    PhasedRelease = 'PhasedRelease',
    AdoptionRate = 'AdoptionRate',
    CrashFreeUsers = 'CrashFreeUsers',
    CrashFreeSessions = 'CrashFreeSessions',
}

export type SummaryViewProperty =
    | SummaryViewPropertyBase
    | typeof ArchetypeSummaryViewProperty
    | typeof StatisticSummaryViewProperty;

// API Response Types
export interface StatusSummaryDataAPI {
    type: ReleaseType;
    status: ReleaseStatus;
    timelinePhase: ReleaseTimelinePhase;
    phasedReleaseInfo?: DestinationPhasedRelease;
}

// Union type for SummaryViewDataRowAPI with specific data types for each property
export type SummaryViewDataRowAPI =
    | { type: typeof SummaryViewPropertyBase.WorkflowRunVersion; data: string }
    | { type: typeof SummaryViewPropertyBase.Status; data: StatusSummaryDataAPI }
    | { type: typeof SummaryViewPropertyBase.ReleasePilot; data: string }
    | { type: typeof SummaryViewPropertyBase.FeatureCompletePercent; data: CompactFeaturesStepData }
    | { type: typeof SummaryViewPropertyBase.TargetKickoffDates; data: string }
    | { type: typeof SummaryViewPropertyBase.TargetSubmitDates; data: string }
    | { type: typeof SummaryViewPropertyBase.TargetReleaseDates; data: string }
    | { type: typeof SummaryViewPropertyBase.KickedOffDate; data: string }
    | { type: typeof SummaryViewPropertyBase.SubmittedDate; data: string }
    | { type: typeof SummaryViewPropertyBase.ReleasedAtDate; data: string }
    | { type: typeof SummaryViewPropertyBase.Builds; data: AppStoreAvailableBuild[] }
    | { type: typeof SummaryViewPropertyBase.PhasedRelease; data: DestinationPhasedRelease }
    | { type: typeof SummaryViewPropertyBase.AdoptionRate; data: AppStoreAvailableBuild[] } // This contains the adoption rate inside each build.
    | { type: typeof ArchetypeSummaryViewProperty; data: StepRunStatusData } // Archetype properties (format: "Archetype:{archetype}")
    | { type: typeof StatisticSummaryViewProperty; data: Statistic }; // Statistic properties (format: "Statistic:{statistic}")

export interface VersionSummaryViewDataItemAPI {
    version: string;
    destinationId: string;
    rows: SummaryViewDataRowAPI[];
}

export interface SummaryViewResponseAPI {
    versionSummaryViewItems: VersionSummaryViewDataItemAPI[];
}

export interface SummaryViewCountResponseAPI {
    totalCount: number;
}

export interface DestinationSummary {
    appId?: string;
    destinationId?: string;
    stepConfigId: string;
    name: string;
    platform: string;
    appStoreType: string;
    iconURL: string;
}

export interface AppDestinationsAndVersions {
    appId: string;
    appName: string;
    destinations: DestinationSummary[];
    versions: string[]; // Limited to 10 most recent per app
    activeVersion?: string;
    liveVersion?: string;
    renderWorkflowsOverview: boolean;
}

export interface OrgDestinationsResponse {
    orgId: string;
    canAccessTabularView: boolean;
    tabularViewDestinationLimit?: number;
    appDestinations: AppDestinationsAndVersions[];
    allDestinationIds: string[];
    allVersions: string[];
}

export const SUPPORTED_STATISTIC_TYPES: StatisticType[] = [
    StatisticType.CIWorkflowSuccessPercent,
    StatisticType.LinesOfCodeChanged,
    StatisticType.ItemsCompleted,
    StatisticType.WaitingForReviewDuration,
    StatisticType.InReviewDuration,
    StatisticType.StatAverageCustomerRating,
];

export const MULTI_INSTANCE_ARCHETYPES = new Set<ReleaseStepType>([ReleaseStepType.BetaTesting]);

export interface SummaryViewTableRow {
    key: string;
    version: string;
    destinationId: string;
    [key: string]: any;
}

export interface SummaryViewRenderContext {
    row: SummaryViewTableRow;
    currentAppId: string;
    appsById: Record<string, any>;
    compact?: boolean;
}

export interface TableMetric {
    key: string;
    title: string;
    renderCell: (context: SummaryViewRenderContext) => React.ReactNode;
    sorter?: (a: SummaryViewTableRow, b: SummaryViewTableRow) => number;
    filterSearch?: boolean;
    filters?: Array<{ text: string; value: string }>;
    onFilter?: (value: string, row: SummaryViewTableRow) => boolean;
    filterDropdown?: ColumnType<SummaryViewTableRow>['filterDropdown'];
    filterIcon?: ColumnType<SummaryViewTableRow>['filterIcon'];
    getCellStyle?: (row: SummaryViewTableRow) => React.CSSProperties;
}

export interface DragOrderPreferences {
    standard?: {
        metricColumnOrder?: string[];
        rowOrder?: string[];
    };
    compact?: {
        destinationColumnOrder?: string[];
        metricRowOrder?: string[];
    };
}

export interface OrgSummaryPreferences {
    pageView: PageViewType;
    tabularLayout: TabularLayoutType;
    selectedMetrics: string[];
    selectedVersions: string[];
    showLive?: boolean;
    showNext?: boolean;
    selectedDestinationIds: string[];
    dragOrderPreferences?: DragOrderPreferences;
}

export const DEFAULT_SUMMARY_PREFERENCES: OrgSummaryPreferences = {
    pageView: PageViewType.Default,
    tabularLayout: TabularLayoutType.Standard,
    selectedMetrics: [],
    selectedVersions: [],
    showLive: false,
    showNext: false,
    selectedDestinationIds: [],
};

export interface SummaryFetchContext {
    kind: 'full' | 'metrics' | 'versions' | 'destinations';
    snapshot: OrgSummaryPreferences;
    source: 'initial' | 'filters';
    metrics?: string[];
    versions?: string[];
    destinations?: string[];
}

export interface UseOrgSummaryParams {
    orgId: string;
    appId?: string;
    availableDestinations?: Destination[];
}

export interface UseSummaryPreferencesProps {
    orgId: string;
    appId?: string;
    availableMetrics: string[];
    availableVersions: string[];
    availableDestinationIds: string[];
}

export interface UseStandardViewColumnsParams {
    metrics: TableMetric[];
    currentAppId: string;
    orgId: string;
    destinationsById: Record<string, Destination>;
    appStoreIntegration?: Integration;
    metricColumnOrder?: string[];
}

export interface UseCompactViewParams {
    releasesData: SummaryViewTableRow[];
    metrics: TableMetric[];
    currentAppId: string;
    orgId: string;
    destinationsById: Record<string, Destination>;
    destinationColumnOrder?: string[];
    metricRowOrder?: string[];
}

export interface OrgSummaryPreferencesSnapshot {
    pageView: PageViewType;
    tabularLayout: TabularLayoutType;
    selectedMetricKeys: string[];
    selectedVersions: string[];
    showLive?: boolean;
    showNext?: boolean;
    selectedDestinationIds: string[];
    dragOrderPreferences?: DragOrderPreferences;
}

export interface FetchSummaryViewParams {
    destinationIds: string[];
    versions: string[];
    properties: string[];
}

export interface FilterOption {
    key: string;
    label: string;
}
